import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
    faVial, 
    faOilCan,
    faFlask,
    faAngleDown, 
    faAngleRight, 
    faExclamationTriangle,
    faInfoCircle,
    faFileMedicalAlt,
    faChartPie
} from '@fortawesome/free-solid-svg-icons';
import './MetabolomeUnifiedCard.css';

/**
 * Unified component for rendering Metabolome analysis data (Plasma, Urine, and Lipids)
 */
const MetabolomeUnifiedCard = ({ data, animationDelay, type = 'plasma' }) => {
    // Debug logging
    console.log(`Metabolome ${type} Card received data:`, data);
    
    const [expanded, setExpanded] = useState(false);
    const [activeSection, setActiveSection] = useState('abnormal');
    
    // Early return if no data
    if (!data) {
        console.error(`Metabolome ${type} Card received no data`);
        return null;
    }
    
    // Configure card based on type
    const cardConfig = {
        plasma: {
            icon: faVial,
            title: 'Plasma Metabolome Analysis',
            className: 's2-metab',
            abnormalKey: 'abnormal_metabolites',
            itemNameKey: 'metabolite_name',
        },
        urine: {
            icon: faFlask,
            title: 'Urine Metabolome Analysis',
            className: 's2-metab-urine',
            abnormalKey: 'abnormal_metabolites',
            itemNameKey: 'metabolite_name',
        },
        lipids: {
            icon: faOilCan,
            title: 'Lipidome Analysis',
            className: 's2-metab-lipids',
            abnormalKey: 'abnormal_lipids',
            itemNameKey: 'lipid_name',
        }
    };
    
    const config = cardConfig[type] || cardConfig.plasma;
    const className = config.className;
    
    // Get details safely
    const details = data.details || {};
    const summary = details.summary || {};
    
    // Get the appropriate abnormal items based on type
    const abnormalItems = details[config.abnormalKey] || [];
    const itemNameKey = config.itemNameKey;
    
    // Calculate category counts
    const categoryCountMap = {};
    abnormalItems.forEach(item => {
        if (item.category) {
            categoryCountMap[item.category] = (categoryCountMap[item.category] || 0) + 1;
        }
    });
    
    // Get status information
    const hasAbnormalItems = abnormalItems.length > 0;
    const statusColor = '#64b5f6';
    
    // Create summary pills
    const summaries = [];
    
    if (abnormalItems.length > 0) {
        const itemType = type === 'lipids' ? 'lipid' : 'metabolite';
        summaries.push({
            text: `${abnormalItems.length} abnormal ${itemType}${abnormalItems.length !== 1 ? 's' : ''}`,
            isWarning: false
        });
        
        // Add category information if available
        const categories = Object.keys(categoryCountMap);
        if (categories.length > 0) {
            const topCategory = categories.sort((a, b) => 
                categoryCountMap[b] - categoryCountMap[a])[0];
            
            summaries.push({
                text: `${categoryCountMap[topCategory]} in ${topCategory}`,
                isWarning: false
            });
        }
    }
    
    return (
        <div className={`${className}-card`} style={{ animationDelay }}>
            <div className={`${className}-header`} onClick={() => setExpanded(!expanded)}>
                <div className={`${className}-icon`}>
                    <FontAwesomeIcon 
                        icon={config.icon} 
                        className={`${className}-icon-${type === 'plasma' ? 'vial' : type === 'urine' ? 'flask' : 'oil'}`}
                    />
                </div>
                <div className={`${className}-title-container`}>
                    <div className={`${className}-title`}>{config.title}</div>
                    
                    {/* Summary Section */}
                    {summaries.length > 0 && (
                        <div className={`${className}-summary`}>
                            {summaries.map((summary, index) => (
                                <div 
                                    key={index} 
                                    className={`${className}-pill ${summary.isWarning ? `${className}-pill-warning` : ''}`}
                                >
                                    {summary.text}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
                <div className={`${className}-expand-icon`}>
                    <FontAwesomeIcon 
                        icon={expanded ? faAngleDown : faAngleRight} 
                    />
                </div>
            </div>
            
            {expanded && (
                <div className={`${className}-content s2-anim-fadeIn`}>
                    {/* WGS-style Summary Section */}
                    <div className={`${className}-wgs-summary`}>
                        <div className={`${className}-summary-row`}>
                            <div className={`${className}-summary-item`}>
                                <div className={`${className}-summary-label`}>{type === 'lipids' ? 'Total Lipids' : 'Total Metabolites'}</div>
                                <div className={`${className}-summary-value`}>
                                    {type === 'lipids' 
                                        ? (summary.total_lipids_analyzed || 0)
                                        : (summary.total_metabolites_analyzed || 0)
                                    }
                                </div>
                            </div>
                            
                            <div className={`${className}-summary-item`}>
                                <div className={`${className}-summary-label`}>{type === 'lipids' ? 'Abnormal Lipids' : 'Abnormal Metabolites'}</div>
                                <div className={`${className}-summary-value`}>
                                    <span className={abnormalItems.length > 0 ? `${className}-abnormal` : ''}>
                                        {type === 'lipids' 
                                            ? (summary.abnormal_lipids_count || 0)
                                            : (summary.abnormal_metabolites_count || 0)
                                        }
                                    </span>
                                </div>
                            </div>
                            
                            <div className={`${className}-summary-item`}>
                                <div className={`${className}-summary-label`}>Categories</div>
                                <div className={`${className}-summary-value`}>
                                    {summary.categories_with_abnormalities || 0}
                                </div>
                            </div>
                        </div>
                    </div>
                    
                    {/* Tab Navigation */}
                    <div className={`${className}-tabs`}>
                        <div 
                            className={`${className}-tab ${activeSection === 'abnormal' ? `${className}-tab-active` : ''}`}
                            onClick={() => setActiveSection('abnormal')}
                        >
                            <FontAwesomeIcon icon={faExclamationTriangle} className={`${className}-tab-icon`} />
                            {type === 'lipids' ? 'Abnormal Lipids' : 'Abnormal Metabolites'}
                        </div>
                    </div>
                    
                    {/* Abnormal Items Section */}
                    {activeSection === 'abnormal' && (
                        <div className={`${className}-abnormal-section`}>
                            {abnormalItems.length > 0 ? (
                                <div className={`${className}-abnormal-grid`}>
                                    {abnormalItems.map((item, index) => {
                                        // Parse reference range
                                        const rangeMatch = item.reference_range.match(/(\\d+\\.?\\d*) - (\\d+\\.?\\d*)/);
                                        const min = rangeMatch ? parseFloat(rangeMatch[1]) : 0;
                                        const max = rangeMatch ? parseFloat(rangeMatch[2]) : 100;
                                        
                                        // Calculate percentage for visualization
                                        const value = parseFloat(item.patient_value);
                                        const normalizedPercentage = Math.min(100, Math.max(0, ((value - min) / (max - min)) * 100));
                                        
                                        // Determine status color
                                        const statusColor = item.status === 'Low' ? '#f44336' : 
                                                           item.status === 'High' ? '#ff9800' : '#64b5f6';
                                        
                                        const itemTypeClass = type === 'lipids' ? 'lipid' : 'metabolite';
                                        
                                        return (
                                            <div key={index} className={`${className}-${itemTypeClass}-item`}>
                                                <div className={`${className}-${itemTypeClass}-header`}>
                                                    <div className={`${className}-${itemTypeClass}-name`}>
                                                        {item[itemNameKey]}
                                                        <div className={`${className}-${itemTypeClass}-formula`}>{item.formula}</div>
                                                    </div>
                                                    <div className={`${className}-${itemTypeClass}-category`}>
                                                        {item.category}
                                                    </div>
                                                </div>
                                                
                                                <div className={`${className}-subsection`}>
                                                    {item.subsection}
                                                </div>
                                                
                                                <div className={`${className}-${itemTypeClass}-status`} style={{ color: statusColor }}>
                                                    {item.status}
                                                </div>
                                                
                                                <div className={`${className}-${itemTypeClass}-bar-container`}>
                                                    <div className={`${className}-${itemTypeClass}-range`}>
                                                        <span className={`${className}-range-min`}>{min}</span>
                                                        <span className={`${className}-range-max`}>{max}</span>
                                                    </div>
                                                    <div className={`${className}-range-bar`}>
                                                        <div 
                                                            className={`${className}-patient-marker`}
                                                            style={{ 
                                                                left: `${normalizedPercentage}%`,
                                                                backgroundColor: statusColor
                                                            }}
                                                        ></div>
                                                    </div>
                                                    <div className={`${className}-${itemTypeClass}-value`}>
                                                        Patient: <span style={{ fontWeight: 'bold' }}>{value.toFixed(2)}</span>
                                                    </div>
                                                </div>
                                                
                                                <div className={`${className}-${itemTypeClass}-id`}>
                                                    ID: {item.sza_id}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            ) : (
                                <div className={`${className}-no-data`}>
                                    No abnormal {type === 'lipids' ? 'lipids' : 'metabolites'} detected
                                </div>
                            )}
                        </div>
                    )}
                </div>
            )}
            
            {data.source && (
                <div className={`${className}-footer`}>
                    <span className={`${className}-source`}>
                        <FontAwesomeIcon icon={faInfoCircle} className={`${className}-source-icon`} />
                        {data.source}
                    </span>
                </div>
            )}
        </div>
    );
};

export default MetabolomeUnifiedCard;
